import {
	useMutation,
	useQuery,
	useQueryClient,
	useTRPC,
	useTRPCClient,
} from "@luckymenu/api-client/react";
import {
	type Area,
	type DiningTable,
	type GeneratedFile,
	type TablesList,
	uuidv7,
} from "@luckymenu/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormErrors } from "../menu/form-errors";
import {
	newTableDraft,
	nextTableLabel,
	type TableDraft,
	type TablesProblem,
	tableDraftToInput,
	tablesByArea,
	tablesProblemOf,
	tableToDraft,
} from "../tables/tables";

function useTablesCache() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	return useMemo(() => {
		const queryKey = trpc.staff.tables.list.queryKey();
		return {
			update: (change: (list: TablesList) => TablesList) =>
				queryClient.setQueryData<TablesList>(queryKey, (old) => (old ? change(old) : old)),
			refresh: () => queryClient.invalidateQueries({ queryKey }),
			/** Drops cached QR images, after a code changed. */
			forgetCodes: () =>
				queryClient.invalidateQueries({ queryKey: trpc.staff.tables.getQrCode.pathKey() }),
		};
	}, [trpc, queryClient]);
}

const upsert = <T extends { id: string }>(list: T[], row: T) =>
	list.some((r) => r.id === row.id) ? list.map((r) => (r.id === row.id ? row : r)) : [...list, row];

/**
 * Areas and tables with their QR URLs, grouped for the screen, and the area
 * actions. Needs `tables.manage` to change anything.
 */
export function useTables() {
	const trpc = useTRPC();
	const cache = useTablesCache();
	const list = useQuery(trpc.staff.tables.list.queryOptions());
	const groups = useMemo(() => (list.data ? tablesByArea(list.data) : []), [list.data]);
	const [problem, setProblem] = useState<{ problem: TablesProblem; areaId: string | null } | null>(
		null,
	);
	const saveArea = useMutation(trpc.staff.tables.upsertArea.mutationOptions());
	const removeArea = useMutation(trpc.staff.tables.deleteArea.mutationOptions());

	return {
		list,
		groups,
		problem,
		clearProblem: () => setProblem(null),
		/** Adds an area, or renames one. Safe to retry: a new area's ID is made here. */
		saveArea: async (name: string, areaId?: string): Promise<Area | null> => {
			setProblem(null);
			try {
				const area = await saveArea.mutateAsync({ id: areaId ?? uuidv7(), name });
				cache.update((l) => ({ ...l, areas: upsert(l.areas, area) }));
				return area;
			} catch (error) {
				setProblem({ problem: tablesProblemOf(error), areaId: areaId ?? null });
				return null;
			}
		},
		/** Deletes an empty area. One with tables is refused with `areaNotEmpty`. */
		deleteArea: async (areaId: string): Promise<boolean> => {
			setProblem(null);
			try {
				await removeArea.mutateAsync({ id: areaId });
				cache.update((l) => ({ ...l, areas: l.areas.filter((a) => a.id !== areaId) }));
				return true;
			} catch (error) {
				setProblem({ problem: tablesProblemOf(error), areaId });
				return false;
			}
		},
		savingArea: saveArea.isPending,
	};
}

/**
 * The table form: `tableId` to edit, or null for a new table in `areaId`
 * with the next label suggested. Also regenerates a table's code.
 */
export function useTableForm({
	tableId,
	areaId = null,
}: {
	tableId: string | null;
	areaId?: string | null;
}) {
	const trpc = useTRPC();
	const cache = useTablesCache();
	const list = useQuery(trpc.staff.tables.list.queryOptions());
	const original: DiningTable | null =
		(tableId && list.data?.tables.find((t) => t.id === tableId)) || null;
	const [draft, setDraft] = useState<TableDraft | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [problem, setProblem] = useState<TablesProblem | null>(null);

	useEffect(() => {
		if (draft || !list.data) return;
		if (tableId) {
			const table = list.data.tables.find((t) => t.id === tableId);
			if (table) setDraft(tableToDraft(table));
			return;
		}
		const inArea = list.data.tables.filter((t) => t.areaId === areaId);
		setDraft(
			newTableDraft(
				nextTableLabel(inArea.length > 0 ? inArea : list.data.tables, list.data.tables),
				areaId,
			),
		);
	}, [draft, list.data, tableId, areaId]);

	const save = useMutation(trpc.staff.tables.upsertTable.mutationOptions());
	const remove = useMutation(trpc.staff.tables.deleteTable.mutationOptions());
	const regenerate = useMutation(trpc.staff.tables.regenerateQr.mutationOptions());

	const set = useCallback(<K extends keyof TableDraft>(field: K, value: TableDraft[K]) => {
		setProblem(null);
		setErrors((e) => {
			if (!(field in e)) return e;
			const next = { ...e };
			delete next[field];
			return next;
		});
		setDraft((d) => (d ? { ...d, [field]: value } : d));
	}, []);

	return {
		status: list.isPending
			? ("loading" as const)
			: tableId && !original
				? ("notFound" as const)
				: !draft
					? ("loading" as const)
					: ("ready" as const),
		isNew: tableId === null,
		original,
		draft,
		errors,
		problem,
		saving: save.isPending,
		deleting: remove.isPending,
		regenerating: regenerate.isPending,
		areas: list.data?.areas ?? [],
		set,
		submit: async (): Promise<DiningTable | null> => {
			if (!draft) return null;
			const result = tableDraftToInput(draft);
			if (!result.ok) {
				setErrors(result.errors);
				return null;
			}
			setErrors({});
			try {
				const table = await save.mutateAsync(result.input);
				cache.update((l) => ({ ...l, tables: upsert(l.tables, table) }));
				setDraft(tableToDraft(table));
				return table;
			} catch (error) {
				const reason = tablesProblemOf(error);
				if (reason === "taken") setErrors({ label: "taken" });
				setProblem(reason);
				return null;
			}
		},
		delete: async (): Promise<boolean> => {
			if (!tableId) return false;
			try {
				await remove.mutateAsync({ id: tableId });
				cache.update((l) => ({ ...l, tables: l.tables.filter((t) => t.id !== tableId) }));
				return true;
			} catch (error) {
				setProblem(tablesProblemOf(error));
				return false;
			}
		},
		/** A new code; the printed one stops working at once (confirm first). */
		regenerate: async (): Promise<DiningTable | null> => {
			if (!tableId) return null;
			try {
				const table = await regenerate.mutateAsync({ tableId });
				cache.update((l) => ({ ...l, tables: upsert(l.tables, table) }));
				void cache.forgetCodes();
				return table;
			} catch (error) {
				setProblem(tablesProblemOf(error));
				return null;
			}
		},
	};
}

export type TableForm = ReturnType<typeof useTableForm>;

/** One table's code to show: SVG by default. Refetched after the code is regenerated. */
export function useQrCode(tableId: string | null, format: "svg" | "png" = "svg") {
	const trpc = useTRPC();
	return useQuery({
		...trpc.staff.tables.getQrCode.queryOptions({ tableId: tableId ?? "", format }),
		enabled: tableId !== null,
		staleTime: Number.POSITIVE_INFINITY,
	});
}

/**
 * Files to save or share: one table's code as PNG or SVG, or the A4 PDF sheet
 * of some tables or every active one. The app saves the bytes (web: a
 * download; Android: a file and the share sheet).
 */
export function useQrFiles() {
	const client = useTRPCClient();
	const [busy, setBusy] = useState<string | null>(null);
	const [problem, setProblem] = useState<TablesProblem | null>(null);
	const run = useCallback(async (key: string, get: () => Promise<GeneratedFile>) => {
		setBusy(key);
		setProblem(null);
		try {
			return await get();
		} catch (error) {
			setProblem(tablesProblemOf(error));
			return null;
		} finally {
			setBusy(null);
		}
	}, []);
	return {
		/** Which file is being made: `code:<tableId>:<format>`, or the sheet's key. */
		busy,
		problem,
		code: (tableId: string, format: "png" | "svg") =>
			run(`code:${tableId}:${format}`, () =>
				client.staff.tables.getQrCode.query({ tableId, format }),
			),
		/** `key` names it in `busy`, such as `sheet:<areaId>`. */
		sheet: (tableIds?: string[], key = "sheet") =>
			run(key, () => client.staff.tables.getQrSheet.query(tableIds ? { tableIds } : {})),
	};
}
